import { Core } from "@faire/design-tokens";
import { ContactFormLinkFormatting } from "@faire/retailer-visitor-shared/components/Links";
import { buildPageLink } from "@faire/retailer-visitor-shared/lib/pathUtils";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { StickySaveProfileButton } from "@faire/retailer-visitor-shared/views/settings/components/StickySaveProfileButton";
import { EmailPreferencesContentWrapper } from "@faire/retailer-visitor-shared/views/settings/email-preferences/components/EmailPreferencesForm";
import { useBrandSentEmailPreferencesQuery } from "@faire/retailer-visitor-shared/views/settings/email-preferences/hooks/useBrandSentEmailPreferencesQuery";
import { useEmailPreferencesQuery } from "@faire/retailer-visitor-shared/views/settings/email-preferences/hooks/useEmailPreferencesQuery";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Pagination } from "@faire/web--source/slate/Pagination";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Toggle } from "@faire/web--source/slate/Toggle";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as useUpdateBrandPreferencesMutation } from "@faire/web-api--source/endpoints/www/api/email/preferences/retailer-brand-preferences/post-hooks";
import { IBrandEmailSubscriptionStatus } from "@faire/web-api--source/com/faire/email/preferences/IBrandEmailSubscriptionStatus";
import { IGetEmailPreferencesResponse } from "@faire/web-api--source/com/faire/email/preferences/IGetEmailPreferencesResponse";
import { IUpdateRetailerBrandEmailPreferencesRequest } from "@faire/web-api--source/com/faire/email/preferences/IUpdateRetailerBrandEmailPreferencesRequest";
import { IUpdateRetailerBrandEmailPreferencesResponse } from "@faire/web-api--source/com/faire/email/preferences/IUpdateRetailerBrandEmailPreferencesResponse";
import { trackRetailerSettingsPageBrandSentEmailsBulkActionClick } from "@faire/web-api--source/events/retailerSettingsPage/click/brandSentEmailsBulkAction";
import { UnsubscribedMarketingType } from "@faire/web-api--source/indigofair/data/UnsubscribedMarketingType";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

interface IProps {
  scrollToTopOfTable: () => void;
}

export const BrandSentEmailPreferences: React.FC<IProps> = ({
  scrollToTopOfTable,
}) => {
  const { emailAddress } = useUserStore(["emailAddress"]);
  const { emailPreferencesResponse, updateEmailPreferencesQuery } =
    useEmailPreferencesQuery();
  const [bulkSubscribeAction, setBulkSubscribeAction] =
    useState<IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus>(
      IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus.NO_ACTION
    );
  const [modifiedBrands, setModifiedBrands] = useState(
    new Map<string, boolean>()
  );
  const [hasJustUpdated, setHasJustUpdated] = useState(false);
  const {
    brandSubscriptionPreferences,
    isLoading,
    isError,
    isFetched,
    removeBrandPreferencesQueriesForAllPages,
  } = useBrandSentEmailPreferencesQuery();

  const handleSuccessfulUpdate = async (
    response: IUpdateRetailerBrandEmailPreferencesResponse
  ) => {
    // update email-preferences query to reflect whether retailer has bulk-actioned subscriptions for all brands
    updateEmailPreferencesQuery(
      (emailPreferences: IGetEmailPreferencesResponse) => {
        if (response.is_unsubscribed_from_all_brands === false) {
          return {
            ...emailPreferences,
            unsubscribed_marketing_types:
              emailPreferences.unsubscribed_marketing_types.filter(
                (type) => type !== UnsubscribedMarketingType.BRAND_CRM_SENT
              ),
          };
        } else if (
          response.is_unsubscribed_from_all_brands &&
          !emailPreferences.unsubscribed_marketing_types.includes(
            UnsubscribedMarketingType.BRAND_CRM_SENT
          )
        ) {
          return {
            ...emailPreferences,
            unsubscribed_marketing_types: [
              ...emailPreferences.unsubscribed_marketing_types,
              UnsubscribedMarketingType.BRAND_CRM_SENT,
            ],
          };
        }
        return emailPreferences;
      }
    );
    removeBrandPreferencesQueriesForAllPages();
    setHasJustUpdated(true);
  };
  const {
    mutateAsync: updateBrandSubscriptionPreferences,
    isPending: isUpdatePending,
  } = useUpdateBrandPreferencesMutation({ onSuccess: handleSuccessfulUpdate });

  useEffect(() => {
    if (hasJustUpdated && isFetched) {
      // reset local changes
      setModifiedBrands(new Map());
      setBulkSubscribeAction(
        IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
          .NO_ACTION
      );
      setHasJustUpdated(false);
    }
  }, [hasJustUpdated, isFetched]);

  const toggleUnsubscribe = useCallback(
    (brandToken: string, toggleValue: boolean) => {
      setModifiedBrands((prevModifiedBrands) => {
        const modifiedBrands = new Map(prevModifiedBrands);
        if (modifiedBrands.has(brandToken)) {
          // toggling a brand that has already been modified reverts to initialState, so remove from modifiedBrands
          modifiedBrands.delete(brandToken);
        } else {
          modifiedBrands.set(brandToken, !toggleValue);
        }
        return modifiedBrands;
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    const brandEmailPreferences = Array.from(
      modifiedBrands,
      ([brandToken, isUnsubscribed]) =>
        IBrandEmailSubscriptionStatus.build({
          brand_token: brandToken,
          unsubscribed: isUnsubscribed,
        })
    );

    return updateBrandSubscriptionPreferences([
      IUpdateRetailerBrandEmailPreferencesRequest.build({
        brand_email_preferences: brandEmailPreferences,
        bulk_action: bulkSubscribeAction,
        email_address: emailAddress,
      }),
    ]);
  }, [
    modifiedBrands,
    updateBrandSubscriptionPreferences,
    emailAddress,
    bulkSubscribeAction,
  ]);

  const handleBulkAction = useCallback(
    (
      bulkAction: IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
    ) => {
      trackRetailerSettingsPageBrandSentEmailsBulkActionClick(
        bulkAction ===
          IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
            .UNSUBSCRIBE_TO_ALL
          ? "opt-out"
          : "opt-in"
      );

      // remove all brand-level edits to be overridden by bulk action status
      setModifiedBrands(new Map());
      setBulkSubscribeAction(bulkAction);
    },
    [setModifiedBrands, setBulkSubscribeAction]
  );

  const getCurrentBrandUnsubscribeStatus = useCallback(
    (brandToken: string, initialUnsubscribeStatus: boolean): boolean => {
      // if any edits have been made, show them from most specific (brand-level) to least specific (bulk action)
      const editedStatus = modifiedBrands.get(brandToken);
      if (isNotUndefined(editedStatus)) {
        return editedStatus;
      }
      if (
        bulkSubscribeAction ===
        IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
          .RESUBSCRIBE_TO_ALL
      ) {
        return false;
      }
      if (
        bulkSubscribeAction ===
        IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
          .UNSUBSCRIBE_TO_ALL
      ) {
        return true;
      }

      // default to initial unsubscribe status before any changes were made
      return initialUnsubscribeStatus;
    },
    [modifiedBrands, bulkSubscribeAction]
  );

  let isCurrentlyUnsubscribedFromAllBrands =
    emailPreferencesResponse?.unsubscribed_marketing_types?.includes(
      UnsubscribedMarketingType.BRAND_CRM_SENT
    );
  if (
    bulkSubscribeAction ===
    IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
      .RESUBSCRIBE_TO_ALL
  ) {
    isCurrentlyUnsubscribedFromAllBrands = false;
  } else if (
    bulkSubscribeAction ===
    IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
      .UNSUBSCRIBE_TO_ALL
  ) {
    isCurrentlyUnsubscribedFromAllBrands = true;
  }

  const isEdited =
    bulkSubscribeAction !==
      IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
        .NO_ACTION || modifiedBrands.size > 0;

  if (isLoading) {
    return (
      <Column align="center">
        <LoadingSpinner />
        <Spacer height="5x" />
      </Column>
    );
  }

  if (isError) {
    return (
      <Column>
        <Typography color={Core.text.critical}>
          <StrictLocalMsg
            defaultMessage="Something went wrong. Please try again or <ContactFormLinkFormatting>contact us</ContactFormLinkFormatting>."
            description={{
              text: "This is displayed to tell the user there was an unhandled error when fetching email preferences.",
            }}
            values={{
              ContactFormLinkFormatting,
            }}
          />
        </Typography>
        <Spacer height="5x" />
      </Column>
    );
  }

  if (!brandSubscriptionPreferences?.brands.length) {
    return (
      <Column>
        <Typography color={Core.text.subdued}>
          <StrictLocalMsg
            defaultMessage="Brands that email you will be listed here."
            description={{
              text: "Message that appears when a retailer has not yet been emailed by any brands, indicating that brands will appear in the table when they have emailed.",
              img: "https://cdn.faire.com/fastly/c42bd3ef5ece9a97c0513d7737066205fb03a9bc2b71445ff8aa8f847760ddfb.png",
            }}
          />
        </Typography>
        <Spacer height="5x" />
      </Column>
    );
  }

  return (
    <>
      <EmailPreferencesContentWrapper>
        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg
            defaultMessage="All brands ({totalBrandsNumber})"
            description={{
              text: "Label at top of table indicating the total number of brands in the table. totalBrandsNumber is an integer, eg 2",
              img: "https://cdn.faire.com/fastly/2c3173789120143f5eec78d8225015f6eb3bede4eb62f43236d5b59e89417fbc.png",
            }}
            values={{
              totalBrandsNumber:
                brandSubscriptionPreferences?.pagination_data?.total_results ??
                "-",
            }}
          />
        </Typography>
        <Spacer height={12} />
        <EmailPreferencesContainer>
          <BrandSubscriptionStatusTableContainer>
            {brandSubscriptionPreferences?.brands.map((brand) => {
              return brand.brand_token ? (
                <BrandSubscriptionStatusRow
                  key={brand.brand_token}
                  brand={brand}
                  isUnsubscribed={getCurrentBrandUnsubscribeStatus(
                    brand.brand_token,
                    !!brand.unsubscribed
                  )}
                  onToggle={(toggleValue) =>
                    toggleUnsubscribe(brand.brand_token!, toggleValue)
                  }
                  disabled={isUpdatePending}
                />
              ) : null;
            })}
          </BrandSubscriptionStatusTableContainer>
          {brandSubscriptionPreferences?.pagination_data ? (
            <StyledPagination
              paginationData={brandSubscriptionPreferences.pagination_data}
              pageToLink={buildPageLink}
              disableButton={isUpdatePending}
              onChangePage={scrollToTopOfTable}
            />
          ) : null}
        </EmailPreferencesContainer>
        <Spacer height="4x" />
        {isCurrentlyUnsubscribedFromAllBrands ? (
          <Button
            variant="tertiary"
            onClick={() =>
              handleBulkAction(
                IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
                  .RESUBSCRIBE_TO_ALL
              )
            }
            disabled={isUpdatePending}
          >
            <StrictLocalMsg
              defaultMessage="Opt in to all emails from brands via Faire"
              description={{
                text: "Button to opt in to receiving emails from all brands via Faire.",
                img: "https://cdn.faire.com/fastly/210577e7723afab21bb9bd6af0c5779b5b4f5d49caf8a87bf82e475e74ddf6d1.png",
              }}
            />
          </Button>
        ) : (
          <Button
            variant="tertiary"
            onClick={() =>
              handleBulkAction(
                IUpdateRetailerBrandEmailPreferencesRequest.BulkSubscribeStatus
                  .UNSUBSCRIBE_TO_ALL
              )
            }
            disabled={isUpdatePending}
          >
            <StrictLocalMsg
              defaultMessage="Opt out of all emails from brands via Faire"
              description={{
                text: "Button to opt out of receiving emails from all brands via Faire.",
                img: "https://cdn.faire.com/fastly/2c3173789120143f5eec78d8225015f6eb3bede4eb62f43236d5b59e89417fbc.png",
              }}
            />
          </Button>
        )}
      </EmailPreferencesContentWrapper>
      <StickySaveProfileButton isVisible={isEdited} onClickPromise={handleSave}>
        <StrictLocalMsg
          defaultMessage="Save"
          description={{
            text: "Button to save changes",
          }}
        />
      </StickySaveProfileButton>
    </>
  );
};

const BrandSubscriptionStatusRow: React.FC<{
  brand: IBrandEmailSubscriptionStatus;
  isUnsubscribed: boolean;
  onToggle: (toggleValue: boolean) => void;
  disabled: boolean;
}> = ({ brand, isUnsubscribed, onToggle, disabled }) => {
  return (
    <StyledRow justify="space-between">
      <Typography variant="paragraphSansMedium">{brand.brand_name}</Typography>
      <Toggle
        value={!isUnsubscribed}
        onChange={onToggle}
        data-test-id={`subscription-status-${brand.brand_token}`}
        disabled={disabled}
        hideText
      />
    </StyledRow>
  );
};

const EmailPreferencesContainer = styled(Column)`
  max-width: 670px;
`;

const BrandSubscriptionStatusTableContainer = styled(Column)`
  border: 1px solid ${Core.border.muted};
`;

const StyledRow = styled(Row)`
  padding: ${getSpacing("2x", 0)};
  margin: ${getSpacing(0, "3x")};
  border-top: 1px solid ${Core.border.muted};
  &:first-of-type {
    border-top: unset;
  }
`;

const StyledPagination = styled(Pagination)`
  margin-top: ${getSpacing("4x")};
  margin-bottom: ${getSpacing("2x")};
`;
